import { Component, ChangeDetectionStrategy, input, output, computed } from '@angular/core';
import { MatIcon } from '@angular/material/icon';

export interface StepBarItem {
  id: string;
  label: string;
  icon?: string;
  state: 'completed' | 'active' | 'upcoming';
  clickable: boolean;
}

@Component({
  selector: 'app-wizard-step-bar',
  imports: [MatIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav class="wsb" role="tablist" [attr.aria-label]="ariaLabel()">
      @for (step of items(); track step.id; let i = $index; let last = $last) {
        <button
          class="wsb-step"
          [class.wsb-step--completed]="step.state === 'completed'"
          [class.wsb-step--active]="step.state === 'active'"
          [class.wsb-step--upcoming]="step.state === 'upcoming'"
          [class.wsb-step--clickable]="step.clickable"
          [disabled]="!step.clickable"
          [attr.aria-selected]="step.state === 'active'"
          [attr.aria-disabled]="!step.clickable"
          role="tab"
          [attr.tabindex]="step.state === 'active' ? 0 : -1"
          (click)="step.clickable && stepClick.emit(i)">
          <span class="wsb-circle">
            @if (step.state === 'completed') {
              <mat-icon class="wsb-check">check</mat-icon>
            } @else {
              <span class="wsb-number">{{ i + 1 }}</span>
            }
          </span>
          <span class="wsb-label">{{ step.label }}</span>
        </button>
        @if (!last) {
          <div class="wsb-line"
               [class.wsb-line--done]="step.state === 'completed'">
          </div>
        }
      }
    </nav>
  `,
  styles: `
    // ─── Wizard Step Bar ─────────────────────────────────
    // Refined horizontal timeline with numbered markers.
    // Uses flex-start alignment for mathematically correct
    // line placement at circle center height.
    // ─────────────────────────────────────────────────────

    $circle: 32px;
    $line-h: 1.5px;
    $done: #2cb5a8;
    $active: var(--secondary-bg-color, #28caf0);
    $muted: #c8cdd8;
    $text: var(--text-color, #2f3033);
    $text-dim: #8c95a6;
    $ease: 250ms cubic-bezier(0.32, 0.72, 0, 1);

    :host { display: block; }

    .wsb {
      display: flex;
      align-items: flex-start;
      padding: 0;
      margin: 0;
      user-select: none;
    }

    // ─── Step Button ─────────────────────────────────────

    .wsb-step {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      border: none;
      background: none;
      padding: 0 14px;
      cursor: default;
      outline: none;
      flex-shrink: 0;
      transition: opacity $ease;

      &--clickable {
        cursor: pointer;

        &:hover .wsb-circle {
          transform: scale(1.06);
        }

        &:focus-visible .wsb-circle {
          outline: 2px solid $active;
          outline-offset: 3px;
        }
      }

      &--upcoming {
        opacity: 0.4;
      }
    }

    // ─── Circle ──────────────────────────────────────────

    .wsb-circle {
      width: $circle;
      height: $circle;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all $ease;
      flex-shrink: 0;

      .wsb-step--upcoming & {
        border: 1.5px solid $muted;
        background: transparent;
        color: $muted;
      }

      .wsb-step--active & {
        border: 2px solid $active;
        background: rgba(40, 202, 240, 0.06);
        color: $active;
        box-shadow: 0 0 0 4px rgba(40, 202, 240, 0.08);
      }

      .wsb-step--completed & {
        border: none;
        background: $done;
        color: #fff;
        box-shadow: 0 1px 3px rgba(44, 181, 168, 0.2);
      }
    }

    .wsb-check {
      font-size: 16px;
      width: 16px;
      height: 16px;
    }

    .wsb-number {
      font-family: 'Poppins', sans-serif;
      font-size: 13px;
      font-weight: 600;
      line-height: 1;
    }

    // ─── Label ───────────────────────────────────────────

    .wsb-label {
      font-family: 'Poppins', sans-serif;
      font-size: 11px;
      font-weight: 500;
      letter-spacing: 0.02em;
      white-space: nowrap;
      text-align: center;
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      color: $text-dim;
      transition: color $ease, font-weight $ease;

      .wsb-step--active & {
        color: $text;
        font-weight: 600;
      }

      .wsb-step--completed & {
        color: $text;
      }
    }

    // ─── Connecting Line ─────────────────────────────────
    // margin-top = circle / 2 to align with circle center.
    // flex-start on the parent makes this simple math.

    .wsb-line {
      flex: 1;
      height: $line-h;
      background: $muted;
      min-width: 20px;
      margin-top: 16px;
      border-radius: 1px;
      transition: background $ease;

      &--done {
        background: $done;
      }
    }

    // ─── Responsive ──────────────────────────────────────

    @media (max-width: 768px) {
      .wsb-label {
        display: none;
      }

      .wsb-step {
        padding: 0 8px;
      }
    }
  `
})
export class WizardStepBarComponent {
  readonly items = input.required<StepBarItem[]>();
  readonly ariaLabel = input('Wizard navigation');
  readonly stepClick = output<number>();

  protected readonly itemCount = computed(() => this.items().length);
}
